import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, IconButton, Tooltip, Avatar, Typography } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import { useReactToPrint } from "react-to-print";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  printHeader: {
    display: "none",
  },
  printContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  }
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const printRef = useRef(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  // Configuração compatível com a API v7/v8 do react-to-print
  // const handlePrint = useReactToPrint({
  //   contentRef: printRef,
  //   documentTitle: `Historico_Ticket_${ticketId}`,
  //   pageStyle: `
  //     @page {
  //       size: A4;
  //       margin: 15mm;
  //     }
  //     @media print {
  //       body {
  //         -webkit-print-color-adjust: exact !important;
  //         print-color-adjust: exact !important;
  //       }
  //       div, main, section {
  //         overflow: visible !important;
  //         height: auto !important;
  //         max-height: none !important;
  //       }
  //       .print-only-header {
  //         display: flex !important;
  //         align-items: center !important;
  //         gap: 15px !important;
  //         padding-bottom: 15px !important;
  //         margin-bottom: 15px !important;
  //         border-bottom: 2px solid #ccc !important;
  //       }
  //       .no-print {
  //         display: none !important;
  //       }
  //     }
  //   `,
  // });
const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Historico_Ticket_${ticketId}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 12mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        /* Libera a rolagem e ajusta a altura para fluxo contínuo */
        html, body, #root, div, main, section {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }

        /* EVITA O CORTE: Impede que mensagens ou imagens quebrem no meio da folha */
        div[class*="messageWrapper"],
        div[class*="messageContainer"],
        li,
        img {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        /* Oculta elementos desnecessários na impressão */
        .no-print {
          display: none !important;
        }

        /* Estilo do cabeçalho do cliente no topo da página 1 */
        .print-only-header {
          display: flex !important;
          align-items: center !important;
          gap: 15px !important;
          padding-bottom: 12px !important;
          margin-bottom: 15px !important;
          border-bottom: 2px solid #ccc !important;
          page-break-after: avoid !important;
        }
      }
    `,
  });
  const triggerPrint = () => {
    if (handlePrint) {
      handlePrint();
    } else {
      // Fallback básico caso o hook falhe
      window.print();
    }
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const handleConnect = () => socket.emit("joinChatBox", ticketId);

    const handleTicket = (data) => {
      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    socket.on("connect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <Tooltip title="Imprimir Histórico">
              <IconButton color="primary" onClick={triggerPrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>

        <ReplyMessageProvider>
          {/* AREA IMPRESSA */}
          <div ref={printRef} className={classes.printContainer}>
            
            {/* Cabeçalho exclusivo para a impressão (Com foto e dados do cliente) */}
            <div className={`${classes.printHeader} print-only-header`}>
              <Avatar 
                src={contact?.profilePicUrl} 
                alt={contact?.name} 
                style={{ width: 60, height: 60 }} 
              />
              <div>
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  {contact?.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {contact?.number}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Ticket #{ticket?.id} - Atendente: {ticket?.user?.name || "N/A"}
                </Typography>
              </div>
            </div>

            {/* Lista de Mensagens do Chat */}
            <MessagesList
              ticketId={ticketId}
              isGroup={ticket.isGroup}
            />
          </div>

          {/* Campo de input de mensagem fica FORA da ref de impressão para não travar o envio/print */}
          <div className="no-print">
            <MessageInput ticketStatus={ticket.status} />
          </div>
        </ReplyMessageProvider>

      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;